import { c as _c } from "react/compiler-runtime";
import type { BoxProps, ButtonProps } from '@chakra-ui/react';
import { Box } from '@chakra-ui/react';
import { ADVISOR_TEAM_UPGRADE_PRODUCT_ID, WIL_AI_UPGRADE_PRODUCT_ID } from '@src/components/sco/Advisors/model/access';
import { ButtonLinkProps } from '@src/components/sco/ButtonLink.tsx';
import type { PaywallGateDensity } from '@src/components/sco/Conversion/PaywallGate';
import { ConversionGate } from '@src/components/sco/Conversion/controller/ConversionGate';
import type { PaywallIntentId } from '@src/components/sco/Conversion/intent';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import React from 'react';

/** Use the preempted button's variant when it is not the default `cta` upgrade style. */
export const inheritUpgradeButtonVariant = (children: React.ReactNode, upgradeCtaButtonProps?: ButtonLinkProps): ButtonLinkProps | undefined => {
  const child = React.Children.toArray(children).find(React.isValidElement) as React.ReactElement<{
    variant?: string;
  }> | undefined;
  const wrappedVariant = child?.props?.variant;
  const inheritVariant = wrappedVariant && wrappedVariant !== 'cta' ? {
    variant: wrappedVariant
  } : undefined;
  if (!inheritVariant && !upgradeCtaButtonProps) {
    return undefined;
  }
  return {
    ...inheritVariant,
    ...upgradeCtaButtonProps
  };
};
type PaywalledAdvisorActionVariant = 'button-only' | 'boxed' | 'page' | 'overlay' | 'blank';
const DENSITY_BY_VARIANT: Record<PaywalledAdvisorActionVariant, PaywallGateDensity> = {
  'button-only': 'button',
  boxed: 'boxed',
  page: 'page',
  overlay: 'overlay',
  blank: 'blank'
};
type PaywalledAdvisorActionProps = {
  children?: React.ReactNode;
  /** The blocked job (apply, send FAST, invite). Defaults to messaging. */
  intent?: PaywallIntentId;
  /** Team advisors stay on the Advisor Team `canAccessAdvisorTeam` entitlement. */
  isTeamAdvisor?: boolean;
  isUnlocked?: boolean;
  /** Headline for boxed / page gates. */
  title?: string;
  upgradeBoxProps?: BoxProps;
  upgradeCtaLabel?: string;
  upgradeCtaButtonProps?: ButtonLinkProps;
  variant?: PaywalledAdvisorActionVariant;
};

/**
 * Startups Pro gate (`canAccessChat`) for marketplace advisor DMs, board invites, applications,
 * and FAST; `isTeamAdvisor` sells Advisor Team (`canAccessAdvisorTeam`). Built on the Conversion
 * gate, so the plan named here is the plan the checkout sells, at its catalog price.
 */
export const PaywalledAdvisorAction = t0 => {
  const $ = _c(29);
  const {
    children,
    intent,
    isTeamAdvisor: t1,
    isUnlocked,
    title,
    upgradeBoxProps,
    upgradeCtaButtonProps,
    upgradeCtaLabel,
    variant: t2
  } = t0;
  const isTeamAdvisor = t1 === undefined ? false : t1;
  const variant = t2 === undefined ? "button-only" : t2;
  const {
    currentUser,
    currentUserHasContentPermission
  } = useCurrentUser();
  let t3;
  if ($[0] !== currentUser?.isAdmin || $[1] !== currentUserHasContentPermission) {
    t3 = !!currentUser?.isAdmin || currentUserHasContentPermission("canAccessAdvisorTeam");
    $[0] = currentUser?.isAdmin;
    $[1] = currentUserHasContentPermission;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const canAccessAdvisorTeam = t3;
  let t4;
  if ($[3] !== canAccessAdvisorTeam || $[4] !== currentUser?.isAdmin || $[5] !== currentUser?.isFounderCommunityMember || $[6] !== currentUser?.isFounderGroupsMember || $[7] !== currentUserHasContentPermission) {
    t4 = !!currentUser?.isAdmin || canAccessAdvisorTeam || currentUserHasContentPermission("canAccessChat") || !!currentUser?.isFounderCommunityMember || !!currentUser?.isFounderGroupsMember;
    $[3] = canAccessAdvisorTeam;
    $[4] = currentUser?.isAdmin;
    $[5] = currentUser?.isFounderCommunityMember;
    $[6] = currentUser?.isFounderGroupsMember;
    $[7] = currentUserHasContentPermission;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const canAccessPaidAdvisorTier = t4;
  let t5;
  if ($[9] !== children || $[10] !== upgradeCtaButtonProps) {
    t5 = inheritUpgradeButtonVariant(children, upgradeCtaButtonProps) || {};
    $[9] = children;
    $[10] = upgradeCtaButtonProps;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = t5 as ButtonLinkProps & {
    href?: string;
  };
  let inheritedCtaProps;
  if ($[12] !== t6) {
    const {
      href: _href,
      ...t7
    } = t6;
    inheritedCtaProps = t7;
    $[12] = t6;
    $[13] = inheritedCtaProps;
  } else {
    inheritedCtaProps = $[13];
  }
  const density = DENSITY_BY_VARIANT[variant];
  const t7 = density === "button";
  const t8 = upgradeCtaLabel || "Upgrade to message";
  const t9 = inheritedCtaProps as ButtonProps;
  const t10 = isTeamAdvisor ? "leadership-message" : intent || "advisor-message";
  const t11 = isUnlocked ?? (isTeamAdvisor ? canAccessAdvisorTeam : canAccessPaidAdvisorTier);
  const t12 = isTeamAdvisor ? "canAccessAdvisorTeam" : "canAccessChat";
  const t13 = isTeamAdvisor ? ADVISOR_TEAM_UPGRADE_PRODUCT_ID : WIL_AI_UPGRADE_PRODUCT_ID;
  let t14;
  if ($[14] !== children || $[15] !== density || $[16] !== t10 || $[17] !== t11 || $[18] !== t12 || $[19] !== t13 || $[20] !== t7 || $[21] !== t8 || $[22] !== t9 || $[23] !== title) {
    t14 = <ConversionGate compactCta={t7} ctaLabel={t8} ctaProps={t9} density={density} headline={title} intent={t10} isUnlocked={t11} permission={t12} productId={t13}>{children}</ConversionGate>;
    $[14] = children;
    $[15] = density;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
    $[19] = t13;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
    $[23] = title;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const gate = t14;
  let t15;
  if ($[25] !== density || $[26] !== gate || $[27] !== upgradeBoxProps) {
    t15 = upgradeBoxProps && density !== "button" ? <Box {...upgradeBoxProps}>{gate}</Box> : gate;
    $[25] = density;
    $[26] = gate;
    $[27] = upgradeBoxProps;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  return t15;
};